// React
import { useEffect, useState } from 'react';

// External Libraries
import { useShallow } from 'zustand/react/shallow';

// Contexts
import { useRoomContext, useWhiteboardContext } from '@src/contexts';

// Services
import SocketService from '@src/services/SocketService';

// Utils
import { checkObjectsEqual } from '@src/utils';

// Socket Listeners
import useCommonSocketListener from './useCommonSocketListener';

// Stores
import {
  useNotificationStore,
  useRoomActionsStore,
  useRoomConfigStore,
  useRoomStore,
  useMagicPenStore,
  useCalculatorStore,
} from '@src/stores';

import { CONFIG_TOGGLE_MAP, MEETING_ENDED } from '@src/constants';

// Types
import { EMeetSocketKeys, ICanvasPayload, IUpdateRoomConfigResponse, TConfigKey } from '@src/types';

const useSocketListener = () => {
  const [isConnected, setIsConnected] = useState(false);

  useCommonSocketListener({ isConnected });

  const { setHasRequestedShare } = useRoomContext();
  const { whiteboardAction, JsonToWbCanvas, handleSaveJsonFromSocket } = useWhiteboardContext();

  const { exitRoomSettings } = useRoomActionsStore(
    useShallow((state) => ({
      exitRoomSettings: state.exitRoomSettings,
    }))
  );

  const { appendNotification } = useNotificationStore(
    useShallow((state) => ({
      appendNotification: state.appendNotification,
    }))
  );

  const { setIsSessionTimedOut } = useRoomStore(
    useShallow((state) => ({
      setIsSessionTimedOut: state.setIsSessionTimedOut,
    }))
  );

  const {
    mainConfig: previousMainConfig,
    chatConfig: previousChatConfig,
    calculatorConfig: previousCalculatorConfig,
    setMainConfig,
    setChatConfig,
    setCalculatorConfig,
    setUserTypeRoomConfig,
  } = useRoomConfigStore(
    useShallow((state) => ({
      mainConfig: state.mainConfig,
      chatConfig: state.chatConfig,
      setMainConfig: state.setMainConfig,
      calculatorConfig: state.calculatorConfig,
      setChatConfig: state.setChatConfig,
      setCalculatorConfig: state.setCalculatorConfig,
      setUserTypeRoomConfig: state.setUserTypeRoomConfig,
    }))
  );

  useEffect(() => {
    const timer = setInterval(() => {
      const isSocketConnected = SocketService.getSocketConnectionState();
      if (isSocketConnected) {
        setIsConnected(isSocketConnected);
        clearInterval(timer);
      }
    }, 500);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!isConnected) {
      return;
    }

    /**
     * Checks if any toggles in the updated config were disabled compared to the previous state,
     * and triggers a notification for each one that is marked with `shouldNotifyOnClose` in CONFIG_TOGGLE_MAP.
     *
     * This is typically used after a config update to inform the user that a setting has been turned off.
     *
     * section: The config section being checked ('main' or 'chat' for now).
     * previousValues: The config values before the update.
     * updatedValues: The new config values after the update.
     */
    const notifyOnDisable = (
      section: 'main' | 'chat',
      previousValues: Record<string, boolean>,
      updatedValues?: Partial<Record<string, boolean>>
    ) => {
      if (!updatedValues || checkObjectsEqual(updatedValues, previousValues)) return;

      Object.entries(updatedValues).forEach(([rawKey, newValue]) => {
        const toggleKey = rawKey as TConfigKey;
        const toggleConfig = CONFIG_TOGGLE_MAP[toggleKey];

        // skip anything that isn’t marked for notification
        if (!toggleConfig || toggleConfig.section !== section || !toggleConfig.shouldNotifyOnClose) return;

        const oldValue = previousValues[toggleKey];
        if (oldValue === true && newValue === false) {
          appendNotification({
            title: '',
            text: `${toggleConfig.prefixLabel} disabled.`,
            source: 'settings',
          });
        }
      });
    };

    const handleUpdateRoomConfig = async (data: Partial<IUpdateRoomConfigResponse>) => {
      if (data?.roomConfig?.main) {
        setMainConfig(data.roomConfig.main);
        Object.values(data.roomConfig.main).length === 1 &&
          notifyOnDisable('main', previousMainConfig, data.roomConfig?.main);
        if (data?.roomConfig?.main?.startScreenButton) {
          setHasRequestedShare(false);
        }
      } else if (data?.roomConfig?.chat) {
        setChatConfig(data.roomConfig.chat);
        Object.values(data.roomConfig.chat).length === 1 &&
          notifyOnDisable('chat', previousChatConfig, data.roomConfig?.chat);
      } else if (data?.roomConfig?.calculator) {
        if (data.roomConfig.calculator.calculatorShowButton === false) {
          useCalculatorStore.getState().setIsPersonalCalculatorVisible(false);

          const sharedCalculatorData = useCalculatorStore.getState().sharedCalculatorData;
          if (sharedCalculatorData.isOpen) {
            useCalculatorStore.getState().resetSharedCalculatorData();
          }
        }
        setCalculatorConfig(data.roomConfig.calculator);
      }

      // Update the configs for userTypes whenever I get a response from updateRoomConfig
      // Update General Settings Switches States for Host and Co-Host
      try {
        const response = await SocketService.request('getRoomConfigByUserType');
        setUserTypeRoomConfig(response);
      } catch (error) {
        console.error('getRoomConfigByUserType error:', error);
      }
    };

    const handleEndSession = (data: { key: string; message: string }) => {
      if ([MEETING_ENDED.MEETING_SESSION_ENDED, MEETING_ENDED.MEETING_ENDED_BY_HOST].includes(data.key)) {
        setIsSessionTimedOut(true);
        exitRoomSettings.onExitClick(true);
      }
    };

    SocketService.on('updateRoomConfig', handleUpdateRoomConfig);
    SocketService.on('end_session', handleEndSession);

    return () => {
      SocketService.off('updateRoomConfig', handleUpdateRoomConfig);
      SocketService.off('end_session', handleEndSession);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  useEffect(() => {
    if (!isConnected) return;

    const handleWhiteboardAction = (data: any) => {
      whiteboardAction(data, false);
    };

    SocketService.on('whiteboardAction', handleWhiteboardAction);

    return () => {
      SocketService.off('whiteboardAction', handleWhiteboardAction);
    };
  }, [isConnected, whiteboardAction]);

  useEffect(() => {
    if (!isConnected) return;
    const peer_id = SocketService.getSocketId();

    const handleWbCanvasToJson = (data: ICanvasPayload | ICanvasPayload[]) => {
      // If we got a batch (someone rejoins), replay absolutely everything:
      const payloadBatch: ICanvasPayload[] = [];
      if (Array.isArray(data)) {
        data.forEach((item) => {
          if (item.canvasData.visible) payloadBatch.push(item);
        });
        handleSaveJsonFromSocket(payloadBatch);
      }

      // Otherwise it's a live, single‐object update—skip only your own echoes:
      else {
        const fromPeerId = data.peerInfo?.peerId;
        if (fromPeerId !== peer_id) {
          JsonToWbCanvas(data);
        }
      }
    };

    SocketService.on('wbCanvasToJson', handleWbCanvasToJson);

    return () => {
      SocketService.off('wbCanvasToJson', handleWbCanvasToJson);
    };
  }, [isConnected, JsonToWbCanvas, handleSaveJsonFromSocket]);

  useEffect(() => {
    if (!isConnected) return;

    const handleConsumerClosed = (data: { consumer_id?: string; consumer_kind?: string; media_type?: string }) => {
      if (data.media_type === 'screenType') {
        useMagicPenStore.getState().clearObjects();
        // If the shared screen is gone, attendees must immediately lose annotate access.
        useRoomStore.getState().setHasMagicPenAnnotatePermission(false);
      }
    };

    SocketService.on(EMeetSocketKeys.CONSUMER_CLOSED, handleConsumerClosed);

    return () => {
      SocketService.off(EMeetSocketKeys.CONSUMER_CLOSED, handleConsumerClosed);
    };
  }, [isConnected]);
};

export default useSocketListener;
